import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Chip, Box } from '@mui/material';
import MDBox from 'components/MDBox';
import { getAutocompleteClients } from 'services/clients';
import { getAutocompleteOperators } from 'services/operators';
import { getAutocompleteBrands } from 'services/brands';

function EntityFilter({ onFilterChange, allowMultiple = false, initialValues = {} }) {
  const [clients, setClients] = useState([]);
  const [operators, setOperators] = useState([]);
  const [brands, setBrands] = useState([]);

  const [selectedClients, setSelectedClients] = useState(initialValues.clientIds || []);
  const [selectedOperators, setSelectedOperators] = useState(initialValues.operatorIds || []);
  const [selectedBrands, setSelectedBrands] = useState(initialValues.brandIds || []);

  // Sync local state with initialValues when they change (e.g., navigation from entity pages)
  useEffect(() => {
    setSelectedClients(initialValues.clientIds || []);
  }, [initialValues.clientIds?.join(',')]);

  useEffect(() => {
    setSelectedOperators(initialValues.operatorIds || []);
  }, [initialValues.operatorIds?.join(',')]);

  useEffect(() => {
    setSelectedBrands(initialValues.brandIds || []);
  }, [initialValues.brandIds?.join(',')]);

  const [loading, setLoading] = useState({
    clients: false,
    operators: false,
    brands: false
  });

  const loadClients = useCallback(async () => {
    setLoading((prev) => ({ ...prev, clients: true }));
    try {
      const data = await getAutocompleteClients('');
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setClients([]);
    }
    setLoading((prev) => ({ ...prev, clients: false }));
  }, []);

  const loadOperators = useCallback(async (clientId = null) => {
    setLoading((prev) => ({ ...prev, operators: true }));
    try {
      // Pass clientId for cascading filter
      const data = await getAutocompleteOperators('', clientId);
      setOperators(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading operators:', err);
      setOperators([]);
    }
    setLoading((prev) => ({ ...prev, operators: false }));
  }, []);

  const loadBrands = useCallback(async (operatorId = null, clientId = null) => {
    setLoading((prev) => ({ ...prev, brands: true }));
    try {
      // Pass operatorId and clientId for cascading filter
      const data = await getAutocompleteBrands('', operatorId, clientId);
      setBrands(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading brands:', err);
      setBrands([]);
    }
    setLoading((prev) => ({ ...prev, brands: false }));
  }, []);

  // Initial load
  useEffect(() => {
    loadClients();
    loadOperators();
    loadBrands();
  }, [loadClients, loadOperators, loadBrands]);

  // Reload operators when client selection changes
  useEffect(() => {
    const clientId = selectedClients.length === 1 ? selectedClients[0] : null;
    loadOperators(clientId);
  }, [selectedClients, loadOperators]);

  // Reload brands when operator or client selection changes
  useEffect(() => {
    const operatorId = selectedOperators.length === 1 ? selectedOperators[0] : null;
    const clientId = selectedClients.length === 1 ? selectedClients[0] : null;
    loadBrands(operatorId, clientId);
  }, [selectedOperators, selectedClients, loadBrands]);

  const getClientNames = useCallback(
    (ids) => {
      return ids
        .map((id) => {
          const client = clients.find((c) => c.value === id);
          return client?.label || null;
        })
        .filter(Boolean);
    },
    [clients]
  );

  const getOperatorNames = useCallback(
    (ids) => {
      return ids
        .map((id) => {
          const operator = operators.find((o) => o.value === id);
          return operator?.label || null;
        })
        .filter(Boolean);
    },
    [operators]
  );

  const getBrandNames = useCallback(
    (ids) => {
      return ids
        .map((id) => {
          const brand = brands.find((b) => b.value === id);
          return brand?.label || null;
        })
        .filter(Boolean);
    },
    [brands]
  );

  const notifyChange = useCallback(
    (newClientIds, newOperatorIds, newBrandIds) => {
      if (onFilterChange) {
        onFilterChange({
          clientIds: newClientIds,
          clientNames: getClientNames(newClientIds),
          operatorIds: newOperatorIds,
          operatorNames: getOperatorNames(newOperatorIds),
          brandIds: newBrandIds,
          brandNames: getBrandNames(newBrandIds)
        });
      }
    },
    [onFilterChange, getClientNames, getOperatorNames, getBrandNames]
  );

  const handleClientChange = (event) => {
    const value = event.target.value;
    const newValue = allowMultiple ? (typeof value === 'string' ? value.split(',') : value) : [value];
    setSelectedClients(newValue);
    setSelectedOperators([]);
    setSelectedBrands([]);
    notifyChange(newValue, [], []);
  };

  const handleOperatorChange = (event) => {
    const value = event.target.value;
    const newValue = allowMultiple ? (typeof value === 'string' ? value.split(',') : value) : [value];
    setSelectedOperators(newValue);
    setSelectedBrands([]);
    notifyChange(selectedClients, newValue, []);
  };

  const handleBrandChange = (event) => {
    const value = event.target.value;
    const newValue = allowMultiple ? (typeof value === 'string' ? value.split(',') : value) : [value];
    setSelectedBrands(newValue);
    notifyChange(selectedClients, selectedOperators, newValue);
  };

  const renderValue = (selected, items) => {
    if (!selected || selected.length === 0) {
      return <span style={{ color: '#999' }}>All</span>;
    }

    const selectedItems = items.filter((item) => selected.includes(item.value));

    // If no valid selections found (items list is empty or selections are stale), show "All"
    if (selectedItems.length === 0) {
      return <span style={{ color: '#999' }}>All</span>;
    }

    if (allowMultiple) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedItems.map((item) => (
            <Chip key={item.value} label={item.label} size='small' sx={{ height: 20, fontSize: '0.7rem' }} />
          ))}
        </Box>
      );
    }

    return selectedItems[0]?.label || '';
  };

  const selectProps = {
    MenuProps: {
      PaperProps: {
        style: {
          maxHeight: 300
        }
      }
    }
  };

  return (
    <MDBox sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size='small'>
        <InputLabel id='client-filter-label' shrink>
          Integration
        </InputLabel>
        <Select
          labelId='client-filter-label'
          id='client-filter'
          multiple={allowMultiple}
          value={allowMultiple ? selectedClients : selectedClients[0] || ''}
          onChange={handleClientChange}
          label='Integration'
          notched
          renderValue={(selected) => renderValue(allowMultiple ? selected : [selected], clients)}
          disabled={loading.clients}
          sx={{ minHeight: 40 }}
          {...selectProps}
        >
          <MenuItem value=''>
            <em>All</em>
          </MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.value} value={client.value}>
              {allowMultiple && <Checkbox checked={selectedClients.includes(client.value)} />}
              <ListItemText primary={client.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size='small'>
        <InputLabel id='operator-filter-label' shrink>
          Operator
        </InputLabel>
        <Select
          labelId='operator-filter-label'
          id='operator-filter'
          multiple={allowMultiple}
          value={allowMultiple ? selectedOperators : selectedOperators[0] || ''}
          onChange={handleOperatorChange}
          label='Operator'
          notched
          renderValue={(selected) => renderValue(allowMultiple ? selected : [selected], operators)}
          disabled={loading.operators}
          sx={{ minHeight: 40 }}
          {...selectProps}
        >
          <MenuItem value=''>
            <em>All</em>
          </MenuItem>
          {operators.map((operator) => (
            <MenuItem key={operator.value} value={operator.value}>
              {allowMultiple && <Checkbox checked={selectedOperators.includes(operator.value)} />}
              <ListItemText primary={operator.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size='small'>
        <InputLabel id='brand-filter-label' shrink>
          Brand
        </InputLabel>
        <Select
          labelId='brand-filter-label'
          id='brand-filter'
          multiple={allowMultiple}
          value={allowMultiple ? selectedBrands : selectedBrands[0] || ''}
          onChange={handleBrandChange}
          label='Brand'
          notched
          renderValue={(selected) => renderValue(allowMultiple ? selected : [selected], brands)}
          disabled={loading.brands}
          sx={{ minHeight: 40 }}
          {...selectProps}
        >
          <MenuItem value=''>
            <em>All</em>
          </MenuItem>
          {brands.map((brand) => (
            <MenuItem key={brand.value} value={brand.value}>
              {allowMultiple && <Checkbox checked={selectedBrands.includes(brand.value)} />}
              <ListItemText primary={brand.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </MDBox>
  );
}

EntityFilter.defaultProps = {
  allowMultiple: false,
  initialValues: {}
};

EntityFilter.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  allowMultiple: PropTypes.bool,
  initialValues: PropTypes.shape({
    clientIds: PropTypes.arrayOf(PropTypes.string),
    operatorIds: PropTypes.arrayOf(PropTypes.string),
    brandIds: PropTypes.arrayOf(PropTypes.string)
  })
};

export default EntityFilter;
